import { useCallback, useEffect, useState } from 'react';
import {
  createMcpServer,
  deleteMcpServer,
  fetchMcpServer,
  fetchMcpServers,
  normalizeMcpDisplayName,
  testMcpServer,
  testMcpServerConfig,
  updateMcpServer,
  validateMcpDisplayName,
  validateMcpDescription,
} from '@/lib/mcpServersApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Edit, Plug, CheckCircle, XCircle, Loader2, ChevronDown } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';

const EMPTY_HEADER_ROW = { key: '', value: '' };

const EMPTY_FORM = {
  display_name: '',
  description: '',
  gateway_url: '',
  auth_type: 'bearer',
  protocol_variant: 'stateless',
  bearer_token: '',
  api_key: '',
  custom_headers: [{ ...EMPTY_HEADER_ROW }],
};

function headersToRows(headers) {
  if (!headers || typeof headers !== 'object') {
    return [{ ...EMPTY_HEADER_ROW }];
  }
  const entries = Object.entries(headers).filter(([k]) => k);
  if (!entries.length) return [{ ...EMPTY_HEADER_ROW }];
  return entries.map(([key, value]) => ({ key, value: String(value ?? '') }));
}

function rowsToHeaders(rows) {
  const out = {};
  for (const row of rows) {
    const key = row.key?.trim();
    const value = row.value?.trim();
    if (key && value) out[key] = value;
  }
  return out;
}

const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'api_key', label: 'API key (x-api-key)' },
  { value: 'bearer_and_api_key', label: 'Bearer + API key' },
];

/** Common headers — user still enters the value; not a full Postman auth builder. */
const HEADER_PRESETS = [
  { key: 'sap-client', hint: 'e.g. 100', group: 'SAP' },
  { key: 'X-CSRF-Token', hint: 'CSRF token from SAP', group: 'SAP' },
  { key: 'Cookie', hint: 'Session cookie if required', group: 'SAP' },
  { key: 'x-api-key', hint: 'API key value', group: 'Gateways' },
  { key: 'Ocp-Apim-Subscription-Key', hint: 'Azure API Management', group: 'Gateways' },
  { key: 'X-Requested-With', hint: 'e.g. XMLHttpRequest', group: 'Other' },
];

function normalizeToolNames(tools) {
  if (!tools?.length) return [];
  return tools.map((tool) => (typeof tool === 'string' ? tool : tool?.name)).filter(Boolean);
}

function ToolList({ tools, compact = false }) {
  const names = normalizeToolNames(tools);
  if (!names.length) return null;
  return (
    <div className={compact ? 'mt-2 space-y-1' : 'mt-3 space-y-2'}>
      <p className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
        Tools ({names.length})
      </p>
      <ul className={`flex flex-wrap gap-1 ${compact ? 'text-xs' : 'text-sm'} max-h-24 overflow-y-auto`}>
        {names.map((name) => (
          <li
            key={name}
            className="font-mono text-muted-foreground border rounded px-2 py-0.5 bg-muted/30"
          >
            {name}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatLastTested(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function connectionStatusMeta(server) {
  const status = server?.last_test_status;
  if (status === 'ok') {
    return {
      label: 'Connected',
      dotClass: 'bg-cp-ok',
      badgeClass: 'bg-cp-ok hover:bg-cp-ok',
      Icon: CheckCircle,
    };
  }
  if (status === 'failed') {
    return {
      label: 'Connection failed',
      dotClass: 'bg-cp-crit',
      badgeClass: 'bg-cp-crit hover:bg-cp-crit',
      Icon: XCircle,
    };
  }
  return {
    label: 'Not tested',
    dotClass: 'bg-cp-faint',
    badgeClass: 'bg-cp-inset hover:bg-cp-inset',
    Icon: null,
  };
}

export default function SettingsMcpServers() {
  const { hasPermission } = usePermissions();
  // Exact actions only — settings.mcp-servers.write is true for create|update|delete
  // and would unlock edit/delete for create-only packs.
  const canCreate = hasPermission('settings.mcp-servers', 'create');
  const canUpdate = hasPermission('settings.mcp-servers', 'update');
  const canDelete = hasPermission('settings.mcp-servers', 'delete');
  /** Connection test hits POST …/test (mutating); require create or update. */
  const canTest = canCreate || canUpdate;
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const canSave = editingId ? canUpdate : canCreate;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testingDialog, setTestingDialog] = useState(false);
  const [dialogTestResult, setDialogTestResult] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [error, setError] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const loadServers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMcpServers();
      setServers(data.servers || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load MCP servers');
      setServers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const openCreate = () => {
    if (!canCreate) return;
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogError(null);
    setDialogTestResult(null);
    setNameError(null);
    setDescriptionError(null);
    setDialogOpen(true);
  };

  const openEdit = async (serverId) => {
    if (!canUpdate) return;
    setError(null);
    setDialogError(null);
    try {
      const row = await fetchMcpServer(serverId);
      setEditingId(serverId);
      setForm({
        display_name: row.display_name || '',
        description: row.description || '',
        gateway_url: row.gateway_url || '',
        auth_type: row.auth_type || 'bearer',
        protocol_variant: row.protocol_variant || 'stateless',
        bearer_token: row.bearer_token || '',
        api_key: row.api_key || '',
        custom_headers: headersToRows(row.headers),
      });
      setDialogOpen(true);
      setDialogTestResult(null);
      setNameError(null);
      setDescriptionError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load MCP server');
    }
  };

  const buildPayload = () => {
    const headers = rowsToHeaders(form.custom_headers);
    const payload = {
      display_name: normalizeMcpDisplayName(form.display_name),
      description: form.description.trim(),
      gateway_url: form.gateway_url.trim(),
      auth_type: form.auth_type,
      protocol_variant: form.protocol_variant,
      headers,
    };
    if (form.bearer_token.trim()) {
      payload.bearer_token = form.bearer_token.trim();
    }
    if (form.api_key.trim()) {
      payload.api_key = form.api_key.trim();
    }
    return payload;
  };

  const handleDialogTest = async () => {
    if (!canTest) return;
    const validationError = validateMcpDisplayName(form.display_name);
    const descError = validateMcpDescription(form.description);
    if (validationError) {
      setNameError(validationError);
      return;
    }
    if (descError) {
      setDescriptionError(descError);
      return;
    }
    setNameError(null);
    setDescriptionError(null);
    setTestingDialog(true);
    setDialogError(null);
    setDialogTestResult(null);
    try {
      const result = await testMcpServerConfig(buildPayload());
      setDialogTestResult(result);
    } catch (err) {
      setDialogError(err?.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingDialog(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    const validationError = validateMcpDisplayName(form.display_name);
    const descError = validateMcpDescription(form.description);
    if (validationError) {
      setNameError(validationError);
      return;
    }
    if (descError) {
      setDescriptionError(descError);
      return;
    }
    setSaving(true);
    setError(null);
    setDialogError(null);
    setNameError(null);
    setDescriptionError(null);
    const payload = buildPayload();

    try {
      let saved;
      if (editingId) {
        saved = await updateMcpServer(editingId, payload);
      } else {
        saved = await createMcpServer(payload);
      }
      setDialogTestResult({
        ok: true,
        tool_count: saved.tool_count ?? saved.tools?.length ?? 0,
      });
      setDialogOpen(false);
      await loadServers();
    } catch (err) {
      const message = err?.response?.data?.detail || 'Failed to save MCP server';
      setDialogError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serverId) => {
    if (!canDelete) return;
    if (!window.confirm('Delete this MCP server? Agent bindings will be removed.')) return;
    setError(null);
    try {
      await deleteMcpServer(serverId);
      await loadServers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete MCP server');
    }
  };

  const handleTest = async (serverId) => {
    if (!canTest) return;
    setTestingId(serverId);
    setTestStatus(null);
    setError(null);
    try {
      const result = await testMcpServer(serverId);
      setTestStatus({ serverId, ...result });
      setServers((prev) =>
        prev.map((server) =>
          server.id === serverId
            ? {
                ...server,
                tool_count: result.tool_count ?? server.tool_count,
                last_test_at: result.last_test_at || server.last_test_at,
                last_test_status: result.ok ? 'ok' : 'failed',
                last_test_message: result.error || result.last_test_message || server.last_test_message,
              }
            : server,
        ),
      );
      await loadServers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const updateHeaderRow = (index, field, value) => {
    setForm((prev) => {
      const custom_headers = prev.custom_headers.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      );
      return { ...prev, custom_headers };
    });
  };

  const addHeaderRow = () => {
    setForm((prev) => ({
      ...prev,
      custom_headers: [...prev.custom_headers, { ...EMPTY_HEADER_ROW }],
    }));
  };

  const removeHeaderRow = (index) => {
    setForm((prev) => {
      const next = prev.custom_headers.filter((_, i) => i !== index);
      return {
        ...prev,
        custom_headers: next.length ? next : [{ ...EMPTY_HEADER_ROW }],
      };
    });
  };

  const headerPlaceholder = (key) =>
    HEADER_PRESETS.find((p) => p.key.toLowerCase() === key.trim().toLowerCase())?.hint || 'Value';

  return (
    <Card data-testid="settings-mcp-servers">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5 text-cp-violet" />
              MCP Servers
            </CardTitle>
            <CardDescription>
              Register remote MCP gateways used by Halo agents. Credentials are stored in Secrets Manager;
              Halo keeps only the mapping and tool list. Test before save, then save runs the same check.
            </CardDescription>
          </div>
          <span title={permissionDeniedTitle(canCreate, 'add MCP servers')}>
            <Button onClick={openCreate} disabled={!canCreate} data-testid="mcp-add-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add MCP Server
            </Button>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{typeof error === 'string' ? error : JSON.stringify(error)}</AlertDescription>
          </Alert>
        )}

        {testStatus && (
          <Alert className={testStatus.ok ? 'bg-cp-inset border-cp-line' : 'bg-cp-inset border-cp-line'}>
            <AlertDescription>
              <div className="flex items-center gap-2">
                {testStatus.ok ? (
                  <CheckCircle className="h-4 w-4 text-cp-ok" />
                ) : (
                  <XCircle className="h-4 w-4 text-cp-crit" />
                )}
                {testStatus.ok
                  ? `Connection OK — ${testStatus.tool_count} tool(s) discovered.`
                  : `Connection failed — ${testStatus.error || testStatus.last_test_message || 'unknown error'}`}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading MCP servers…
          </div>
        ) : servers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Plug className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>No MCP servers configured yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {servers.map((server) => {
              const statusMeta = connectionStatusMeta(server);
              const StatusIcon = statusMeta.Icon;
              const lastTested = formatLastTested(server.last_test_at);

              return (
              <div
                key={server.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border rounded-lg"
                data-testid={`mcp-server-row-${server.id}`}
              >
                <div className="space-y-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusMeta.dotClass}`}
                      title={statusMeta.label}
                      aria-hidden
                    />
                    <div className="font-semibold">{server.display_name}</div>
                    <Badge className={statusMeta.badgeClass}>
                      {StatusIcon && <StatusIcon className="h-3 w-3 mr-1" />}
                      {statusMeta.label}
                    </Badge>
                  </div>
                  {server.description && (
                    <p className="text-sm text-muted-foreground">{server.description}</p>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {lastTested ? (
                      <span>Last tested: {lastTested}</span>
                    ) : (
                      <span>Connection not verified yet.</span>
                    )}
                    {server.last_test_status === 'failed' && server.last_test_message && (
                      <span className="block text-cp-crit mt-0.5">{server.last_test_message}</span>
                    )}
                  </div>
                  <ToolList tools={server.tools} compact />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span title={permissionDeniedTitle(canTest, 'test MCP servers')}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(server.id)}
                      disabled={!canTest || testingId === server.id}
                    >
                      {testingId === server.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Test'
                      )}
                    </Button>
                  </span>
                  <span title={permissionDeniedTitle(canUpdate, 'edit MCP servers')}>
                    <Button variant="outline" size="sm" disabled={!canUpdate} onClick={() => openEdit(server.id)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </span>
                  <span title={permissionDeniedTitle(canDelete, 'delete MCP servers')}>
                    <Button variant="outline" size="sm" disabled={!canDelete} onClick={() => handleDelete(server.id)}>
                      <Trash2 className="h-4 w-4 text-cp-crit" />
                    </Button>
                  </span>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit MCP Server' : 'Add MCP Server'}</DialogTitle>
            <DialogDescription>
              URL should point at the hosted MCP HTTP endpoint. We append <code>/mcp</code> if missing.
              Save runs <code>initialize</code> + <code>tools/list</code> — the server is only stored if that succeeds.
            </DialogDescription>
          </DialogHeader>
          {dialogError && (
            <Alert variant="destructive">
              <AlertDescription>{dialogError}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="mcp-name">Display name</Label>
              <Input
                id="mcp-name"
                placeholder="sap_odata_dev"
                value={form.display_name}
                onChange={(e) => {
                  const value = normalizeMcpDisplayName(e.target.value);
                  setForm({ ...form, display_name: value });
                  setNameError(validateMcpDisplayName(value));
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lowercase letters, digits, and underscores only. Must start with a letter.
              </p>
              {nameError && <p className="text-xs text-cp-crit mt-1">{nameError}</p>}
            </div>
            <div>
              <Label htmlFor="mcp-desc">Description</Label>
              <Textarea
                id="mcp-desc"
                placeholder="What this MCP does — agents use this to decide when to call it"
                value={form.description}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm({ ...form, description: value });
                  setDescriptionError(validateMcpDescription(value));
                }}
                rows={2}
                required
              />
              {descriptionError && (
                <p className="text-xs text-cp-crit mt-1">{descriptionError}</p>
              )}
            </div>
            <div>
              <Label htmlFor="mcp-url">Gateway URL</Label>
              <Input
                id="mcp-url"
                placeholder="https://mcp.example.com/sap-odata/mcp"
                value={form.gateway_url}
                onChange={(e) => setForm({ ...form, gateway_url: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Auth type</Label>
                <Select
                  value={form.auth_type}
                  onValueChange={(value) => setForm({ ...form, auth_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTH_TYPES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Protocol</Label>
                <Select
                  value={form.protocol_variant}
                  onValueChange={(value) => setForm({ ...form, protocol_variant: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stateless">Stateless HTTP</SelectItem>
                    <SelectItem value="stateful">Stateful HTTP</SelectItem>
                    <SelectItem value="auto">Auto-detect</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.protocol_variant === 'stateful'
                    ? 'Server keeps an MCP session across calls (streamable HTTP).'
                    : form.protocol_variant === 'auto'
                      ? 'Try stateless first, then stateful if the server requires a session.'
                      : 'Each request is independent — use for most OData / gateway MCP servers.'}
                </p>
              </div>
            </div>
            {form.auth_type !== 'none' && (
              <>
                {(form.auth_type === 'bearer' || form.auth_type === 'bearer_and_api_key') && (
                  <div>
                    <Label htmlFor="mcp-bearer">Bearer token</Label>
                    <Input
                      id="mcp-bearer"
                      type="password"
                      autoComplete="off"
                      value={form.bearer_token}
                      onChange={(e) => setForm({ ...form, bearer_token: e.target.value })}
                    />
                  </div>
                )}
                {(form.auth_type === 'api_key' || form.auth_type === 'bearer_and_api_key') && (
                  <div>
                    <Label htmlFor="mcp-api-key">API key</Label>
                    <Input
                      id="mcp-api-key"
                      type="password"
                      autoComplete="off"
                      value={form.api_key}
                      onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                    />
                  </div>
                )}
              </>
            )}
            <div className="space-y-2">
              <Label>Custom headers</Label>
              <p className="text-xs text-muted-foreground">
                Optional. Type a header name or pick from the list — then enter the value.
              </p>
              <div className="space-y-2">
                {form.custom_headers.map((row, index) => (
                  <div key={`header-${index}`} className="flex gap-2 items-center">
                    <div className="flex flex-1 min-w-0 items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                      <Input
                        list="mcp-header-presets"
                        placeholder="Header name"
                        value={row.key}
                        onChange={(e) => updateHeaderRow(index, 'key', e.target.value)}
                        className="flex-1 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                      <Select onValueChange={(presetKey) => updateHeaderRow(index, 'key', presetKey)}>
                        <SelectTrigger
                          className="w-9 shrink-0 border-0 border-l rounded-none shadow-none focus:ring-0 [&>svg:last-child]:hidden"
                          aria-label="Pick suggested header name"
                        >
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </SelectTrigger>
                        <SelectContent align="end">
                          {['SAP', 'Gateways', 'Other'].map((group) => (
                            <SelectGroup key={group}>
                              <SelectLabel>{group}</SelectLabel>
                              {HEADER_PRESETS.filter((p) => p.group === group).map((preset) => (
                                <SelectItem key={preset.key} value={preset.key}>
                                  {preset.key}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder={headerPlaceholder(row.key)}
                      value={row.value}
                      onChange={(e) => updateHeaderRow(index, 'value', e.target.value)}
                      className="flex-1"
                    />
                    {form.custom_headers.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeHeaderRow(index)}
                        aria-label="Remove header"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <datalist id="mcp-header-presets">
                  {HEADER_PRESETS.map((preset) => (
                    <option key={preset.key} value={preset.key} />
                  ))}
                </datalist>
                <Button type="button" variant="outline" size="sm" onClick={addHeaderRow} className="mt-1">
                  <Plus className="h-3 w-3 mr-1" />
                  Add header
                </Button>
              </div>
            </div>
          </div>
          {dialogTestResult && (
            <Alert className={dialogTestResult.ok ? 'bg-cp-inset border-cp-line' : 'bg-cp-inset border-cp-line'}>
              <AlertDescription>
                {dialogTestResult.ok
                  ? `Connection OK — ${dialogTestResult.tool_count} tool(s) discovered.`
                  : `Connection failed — ${dialogTestResult.error || 'unknown error'}`}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-between gap-2">
            <span title={permissionDeniedTitle(canTest, 'test MCP servers')}>
              <Button
                type="button"
                variant="outline"
                onClick={handleDialogTest}
                disabled={!canTest || testingDialog || !form.display_name || !form.description.trim() || !form.gateway_url || !!nameError || !!descriptionError}
              >
                {testingDialog ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing…
                  </>
                ) : (
                  'Test connection'
                )}
              </Button>
            </span>
            <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave || saving || !form.display_name || !form.description.trim() || !form.gateway_url || !!nameError || !!descriptionError}
              title={permissionDeniedTitle(canSave, editingId ? 'update MCP servers' : 'add MCP servers')}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying connection…
                </>
              ) : (
                'Save'
              )}
            </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
